import React, {Suspense} from 'react';
import {Link, useLocation, useNavigate} from "react-router-dom";
import {Box, Divider, IconButton, Stack, Tab, Tabs, Typography} from "@mui/material";
import {Theme, ThemeProvider} from "@mui/material/styles";
import ApartmentIcon from "@mui/icons-material/ApartmentOutlined";
import PeopleIcon from "@mui/icons-material/PeopleAltOutlined";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBusOutlined";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import SensorsOffIcon from "@mui/icons-material/SensorsOffOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useTranslation} from "react-i18next";
import PageLayout from "@view/layout/pageLayout/PageLayout";
import SpinnerCustom from "@ui/spinner/SpinnerCustom";
import NotItems from "@ui/notItems/NotItems";
import TooltipCustom from "@ui/tooltipCustom/TooltipCustom";
import AccountAccentScope from "@containers/accountStyle/scope/AccountAccentScope";
import {useGetUserAccessWidthState} from "@containers/account/access/hooks/useGetUserAccess";
import {
	PATH_ADMIN_ACCOUNTS,
	PATH_ADMIN_DEVICES,
	PATH_ADMIN_EMPLOYEES,
	PATH_ADMIN_UNREGISTERED,
	PATH_ADMIN_USERS
} from "@constants/navigate";

// The tabs of the legacy system pages, in their order; billing stays on track.
const TABS = [
	{path: PATH_ADMIN_ACCOUNTS, label: 'adminSystem.tabAccounts', title: 'adminAccounts.title', icon: <ApartmentIcon/>},
	{path: PATH_ADMIN_USERS, label: 'adminSystem.tabUsers', title: 'adminSystem.tabUsersTitle', icon: <PeopleIcon/>},
	{path: PATH_ADMIN_DEVICES, label: 'adminSystem.tabDevices', title: 'adminSystem.tabDevicesTitle', icon: <DirectionsBusIcon/>},
	{path: PATH_ADMIN_EMPLOYEES, label: 'adminSystem.tabEmployees', title: 'adminSystem.tabEmployeesTitle', icon: <BadgeIcon/>},
	{path: PATH_ADMIN_UNREGISTERED, label: 'adminSystem.tabUnregistered', title: 'adminSystem.tabUnregisteredTitle', icon: <SensorsOffIcon/>},
]

const withSize = (outer: Theme, name: string) => {
	const component = (outer.components as Record<string, { defaultProps?: object }> | undefined)?.[name]
	return {...component, defaultProps: {...component?.defaultProps, size: 'small'}}
}

// The admin pages hold many fields: small ones fit a form on a screen.
const compactTheme = (outer: Theme): Theme => ({
	...outer,
	components: {
		...outer.components,
		MuiTextField: withSize(outer, 'MuiTextField'),
		MuiFormControl: withSize(outer, 'MuiFormControl'),
		MuiAutocomplete: withSize(outer, 'MuiAutocomplete'),
	} as Theme['components'],
})

const Heading = () => {
	const {t} = useTranslation()
	const {pathname, key} = useLocation()
	const navigate = useNavigate()
	const current = TABS.find((tab) => pathname.startsWith(tab.path)) ?? TABS[0]
	// A card or a form under a tab, not the list of the tab itself.
	const nested = pathname.replace(/\/$/, '') !== current.path

	// Back where the card was opened from; a link opened directly falls back to the list.
	const back = () => key !== 'default' ? navigate(-1) : navigate(current.path)

	return (
		<Stack gap={0.75}>
			<Typography component={'h1'} sx={{fontSize: 18, fontWeight: 700, lineHeight: 1.3}}>
				{t(current.title)}
			</Typography>
			<Stack direction={'row'} alignItems={'center'} sx={{borderBottom: 1, borderColor: 'divider'}}>
				{nested && <>
					<TooltipCustom title={t('adminSystem.back')}>
						<IconButton size={'small'} onClick={back} sx={{mr: 0.5}}>
							<ArrowBackIcon fontSize={'small'}/>
						</IconButton>
					</TooltipCustom>
					<Divider orientation={'vertical'} flexItem sx={{my: 1, mr: 0.5}}/>
				</>}
				<Tabs
					value={current.path}
					variant={'scrollable'}
					scrollButtons={'auto'}
					sx={{
						minHeight: 38,
						'& .MuiTab-root': {minHeight: 38, textTransform: 'none', fontWeight: 600, fontSize: 13.5, px: 1.5},
						'& .MuiTab-root .MuiSvgIcon-root': {fontSize: 19},
						'& .MuiTabs-indicator': {height: 3, borderRadius: '3px 3px 0 0'},
					}}
				>
					{TABS.map((tab) => <Tab
						key={tab.path}
						value={tab.path}
						component={Link}
						to={tab.path}
						label={t(tab.label)}
						icon={tab.icon}
						iconPosition={'start'}
					/>)}
				</Tabs>
			</Stack>
		</Stack>
	)
}

/** The frame of the system administrator's pages: account colour, the tabs, and the role check. */
const AdminSystemLayout = ({children}: { children: React.ReactNode }) => {

	const {t} = useTranslation()
	// track-api answers 403 to anyone else; this only spares them an empty page.
	const {access} = useGetUserAccessWidthState({keys: 'ROLE_TENANT_ADMIN'})

	return (
		<PageLayout>
			<AccountAccentScope>
				<ThemeProvider theme={compactTheme}>
					{access
						? <Box sx={{bgcolor: 'background.paper', borderRadius: 3, p: {xs: 1.5, md: 2}, flex: 1}}>
							<Stack gap={1.5} sx={{width: '100%'}}>
								<Heading/>
								<Suspense fallback={<SpinnerCustom/>}>
									{children}
								</Suspense>
							</Stack>
						</Box>
						: <NotItems message={t('adminAccounts.noAccess')}/>}
				</ThemeProvider>
			</AccountAccentScope>
		</PageLayout>
	)
}

export default AdminSystemLayout;
